"use client"

import { createContext, useContext, useReducer, ReactNode, useEffect, useState, useRef, useCallback } from "react"
import { 
  addToCart as addToCartAPI, 
  updateCartItem as updateCartItemAPI, 
  removeFromCart as removeFromCartAPI, 
  clearCart as clearCartAPI, 
  getCart as getCartAPI,
  calculateDiscountedPrice,
  isDiscountActive
} from "@/lib/utils"
import { useAuth } from "./use-auth"
import { useToast } from "./use-toast"

interface CartItem {
  id: number
  name: string
  price: number
  originalPrice?: number
  image: string
  quantity: number
  inStock: boolean
  is_vatable?: boolean
}

interface CartState {
  items: CartItem[]
  isOpen: boolean
  isSyncing: boolean
  lastSynced: Date | null
  isLoading: boolean // New loading state for cart operations
  hasInitialized: boolean // Track if initial sync has been done
}

type CartAction =
  | { type: "ADD_ITEM"; payload: CartItem }
  | { type: "REMOVE_ITEM"; payload: number }
  | { type: "UPDATE_QUANTITY"; payload: { id: number; quantity: number } }
  | { type: "CLEAR_CART" }
  | { type: "TOGGLE_CART" }
  | { type: "CLOSE_CART" }
  | { type: "SET_ITEMS"; payload: CartItem[] }
  | { type: "SET_SYNCING"; payload: boolean }
  | { type: "SET_LAST_SYNCED"; payload: Date }
  | { type: "SET_LOADING"; payload: boolean } // New action for loading state
  | { type: "SET_INITIALIZED"; payload: boolean } // Track initialization

const CartContext = createContext<{
  state: CartState
  addItem: (item: Omit<CartItem, "quantity">) => Promise<void>
  removeItem: (id: number) => Promise<void>
  updateQuantity: (id: number, quantity: number) => Promise<void>
  clearCart: () => Promise<void>
  toggleCart: () => void
  closeCart: () => void
  getTotalItems: () => number
  getSubtotal: () => number
  getTax: () => number
  getTotal: () => number
  syncWithBackend: () => Promise<void>
  isAuthenticated: boolean
} | null>(null)

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case "ADD_ITEM": {
      const existingItem = state.items.find(item => item.id === action.payload.id)
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        }
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, quantity: 1 }]
      }
    }
    case "REMOVE_ITEM":
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      }
    case "UPDATE_QUANTITY":
      if (action.payload.quantity === 0) {
        return {
          ...state,
          items: state.items.filter(item => item.id !== action.payload.id)
        }
      }
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      }
    case "CLEAR_CART":
      return {
        ...state,
        items: []
      }
    case "SET_ITEMS":
      return {
        ...state,
        items: action.payload
      }
    case "SET_SYNCING":
      return {
        ...state,
        isSyncing: action.payload
      }
    case "SET_LAST_SYNCED":
      return {
        ...state,
        lastSynced: action.payload
      }
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload
      }
    case "SET_INITIALIZED":
      return {
        ...state,
        hasInitialized: action.payload
      }
    case "TOGGLE_CART":
      return {
        ...state,
        isOpen: !state.isOpen
      }
    case "CLOSE_CART":
      return {
        ...state,
        isOpen: false
      }
    default:
      return state
  }
}

// Helper function to save cart to localStorage
const saveCartToStorage = (items: CartItem[]) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('cart', JSON.stringify(items))
  }
}

// Helper function to load cart from localStorage
const loadCartFromStorage = (): CartItem[] => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('cart')
    return stored ? JSON.parse(stored) : []
  }
  return []
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, {
    items: loadCartFromStorage(),
    isOpen: false,
    isSyncing: false,
    lastSynced: null,
    isLoading: false,
    hasInitialized: false
  })
  
  const { state: authState } = useAuth()
  const { toast } = useToast()
  const isAuthenticated = authState.isAuthenticated
  
  // Use refs to track sync state and prevent multiple simultaneous syncs
  const syncInProgress = useRef(false)
  const lastSyncTime = useRef<number>(0)
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Save cart to localStorage whenever items change
  useEffect(() => {
    saveCartToStorage(state.items)
  }, [state.items])

  // Debounced sync function to prevent excessive API calls
  const debouncedSync = useCallback(async () => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current)
    }
    
    syncTimeoutRef.current = setTimeout(async () => {
      // Prevent multiple simultaneous syncs
      if (syncInProgress.current) {
        console.log('Sync already in progress, skipping...')
        return
      }
      
      // Prevent syncs that are too frequent (less than 2 seconds apart)
      const now = Date.now()
      if (now - lastSyncTime.current < 2000) {
        console.log('Sync too frequent, skipping...')
        return
      }
      
      await syncWithBackend()
    }, 500) // 500ms debounce
  }, [])

  // Sync with backend when authenticated
  const syncWithBackend = async () => {
    if (!isAuthenticated || syncInProgress.current) return

    try {
      syncInProgress.current = true
      lastSyncTime.current = Date.now()
      
      dispatch({ type: "SET_SYNCING", payload: true })
      const backendCart = await getCartAPI()
      
      console.log('Backend cart response:', backendCart)
      
      // Handle the new cart structure where items are in backendCart.items
      const cartItems = backendCart.items || backendCart.data || []
      
      // Map backend cart items to frontend format
      const mappedItems = cartItems.map((item: any) => ({
        id: item.product_id || item.id,
        name: item.name || item.product_name,
        price: Number(item.price || item.selling_price || 0),
        originalPrice: item.original_price || item.compare_price,
        image: item.image,
        quantity: Number(item.quantity || 1),
        inStock: item.in_stock ?? true,
        is_vatable: item.is_vatable ?? false,
      }))

      console.log('Mapped cart items:', mappedItems)
      dispatch({ type: "SET_ITEMS", payload: mappedItems })
      dispatch({ type: "SET_LAST_SYNCED", payload: new Date() })
      dispatch({ type: "SET_INITIALIZED", payload: true })
    } catch (error) {
      console.error('Failed to sync cart with backend:', error)
    } finally {
      dispatch({ type: "SET_SYNCING", payload: false })
      syncInProgress.current = false
    }
  }

  // Sync with backend on mount if authenticated (only once)
  useEffect(() => {
    console.log('Auth state changed:', { isAuthenticated, hasInitialized: state.hasInitialized })
    if (isAuthenticated && !state.hasInitialized) {
      console.log('Initial cart sync triggered...')
      debouncedSync()
    } else if (isAuthenticated && state.hasInitialized) {
      console.log('Cart already initialized, skipping sync')
    } else if (!isAuthenticated) {
      console.log('User not authenticated, skipping sync')
    }
  }, [isAuthenticated, state.hasInitialized, debouncedSync])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }
  }, [])

  const addItem = async (item: Omit<CartItem, "quantity">) => {
    // Set loading state
    dispatch({ type: "SET_LOADING", payload: true })

    try {
      // Apply discount from canonical/base price.
      // Some pages already pass discounted `item.price`; prefer `originalPrice` when present.
      const originalPrice = Number(
        item.originalPrice && Number(item.originalPrice) > 0 ? item.originalPrice : item.price
      )
      const { discountedPrice, discountAmount } = calculateDiscountedPrice(originalPrice)
      const isDiscountApplied = isDiscountActive() && discountAmount > 0
      
      const cartItem = {
        ...item,
        price: isDiscountApplied ? discountedPrice : originalPrice,
        originalPrice: isDiscountApplied ? originalPrice : item.originalPrice,
        quantity: 1
      }

      // Update local state immediately
      dispatch({ type: "ADD_ITEM", payload: cartItem })

      // Sync with backend if authenticated
      if (isAuthenticated) {
        try {
          const res = await addToCartAPI(item.id, 1)
          console.log('addToCartAPI response:', res)
        } catch (error) {
          console.error('Failed to add item to backend cart:', error)
          // Show error toast but don't return - keep item in local cart
          toast({
            title: "Warning",
            description: "Item added to local cart. Will sync when connection is restored.",
            variant: "default",
          })
        }
      } else {
        // Show success toast for local storage
        toast({
          title: "Added to Cart",
          description: `${item.name} has been added to your cart. Login to sync with your account.`,
        })
      }

    } catch (error) {
      console.error('Error adding item to cart:', error)
      toast({
        title: "Error",
        description: "Failed to add item to cart. Please try again.",
        variant: "destructive",
      })
    } finally {
      // Reset loading state
      dispatch({ type: "SET_LOADING", payload: false })
    }
  }

  const removeItem = async (id: number) => {
    dispatch({ type: "SET_LOADING", payload: true })

    try {
      // Update local state immediately
      dispatch({ type: "REMOVE_ITEM", payload: id })

      // Sync with backend if authenticated
      if (isAuthenticated) {
        try {
          await removeFromCartAPI(id)
        } catch (error) {
          console.error('Failed to remove item from backend cart:', error)
          toast({
            title: "Error",
            description: "Failed to remove item from cart. Please try again.",
            variant: "destructive",
          })
        }
      }
    } catch (error) {
      console.error('Error removing item from cart:', error)
      toast({
        title: "Error",
        description: "Failed to remove item from cart. Please try again.",
        variant: "destructive",
      })
    } finally {
      dispatch({ type: "SET_LOADING", payload: false })
    }
  }

  const updateQuantity = async (id: number, quantity: number) => {
    dispatch({ type: "SET_LOADING", payload: true })

    try {
      // Update local state immediately
      dispatch({ type: "UPDATE_QUANTITY", payload: { id, quantity } })

      // Sync with backend if authenticated
      if (isAuthenticated) {
        try {
          await updateCartItemAPI(id, quantity)
        } catch (error) {
          console.error('Failed to update item in backend cart:', error)
          toast({
            title: "Error",
            description: "Failed to update cart. Please try again.",
            variant: "destructive",
          })
        }
      }
    } catch (error) {
      console.error('Error updating cart quantity:', error)
      toast({
        title: "Error",
        description: "Failed to update cart. Please try again.",
        variant: "destructive",
      })
    } finally {
      dispatch({ type: "SET_LOADING", payload: false })
    }
  }

  const clearCart = async () => {
    dispatch({ type: "SET_LOADING", payload: true })

    try {
      // Update local state immediately
      dispatch({ type: "CLEAR_CART" })

      // Sync with backend if authenticated
      if (isAuthenticated) {
        try {
          await clearCartAPI()
        } catch (error) {
          console.error('Failed to clear backend cart:', error)
          toast({
            title: "Error",
            description: "Failed to clear cart. Please try again.",
            variant: "destructive",
          })
        }
      }
    } catch (error) {
      console.error('Error clearing cart:', error)
      toast({
        title: "Error",
        description: "Failed to clear cart. Please try again.",
        variant: "destructive",
      })
    } finally {
      dispatch({ type: "SET_LOADING", payload: false })
    }
  }

  const toggleCart = () => {
    dispatch({ type: "TOGGLE_CART" })
  }

  const closeCart = () => {
    dispatch({ type: "CLOSE_CART" })
  }

  const getTotalItems = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0)
  }

  const getSubtotal = () => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0)
  }

  const getTax = () => {
    return state.items.reduce((total, item) => {
      if (item.is_vatable) {
        return total + (item.price * item.quantity * 0.16);
      }
      return total;
    }, 0);
  };

  const getTotal = () => {
    return getSubtotal() + getTax();
  };

  return (
    <CartContext.Provider
      value={{
        state,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        toggleCart,
        closeCart,
        getTotalItems,
        getSubtotal,
        getTax,
        getTotal,
        syncWithBackend,
        isAuthenticated
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error("useCart must be used within a CartProvider")
  }
  return context
}